import React, { useState, useEffect } from 'react';
import { useBrand } from '../../contexts/BrandContext';
import { Make_Authorized_API_Call, isSuccessResponse } from '../../api/apiClient';
import { API_ENDPOINTS } from '../../services/api/endpoints';

// Note: AgencyFlow metrics endpoint pending backend implementation in template-core.yaml

interface ClientSummary {
  Client_ID: string;
  Client_Name: string;
  status: 'active' | 'paused' | 'completed';
  activeProjects: number;
  totalRevenue: number;
  hoursThisMonth: number;
  utilizationRate: number;
}

interface PortfolioMetrics {
  totalClients: number;
  activeProjects: number;
  totalRevenue: number;
  avgUtilization: number;
  billableHours: number;
  nonBillableHours: number;
}

interface RevenueMetrics {
  currentMonth: number;
  previousMonth: number;
  forecast: number;
  mrr: number;
}

const AgencyFlowDashboard: React.FC = () => {
  const { primaryColor, brand } = useBrand();
  const brandColor = brand?.Primary_Color || primaryColor || '#F59E0B';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [clients, setClients] = useState<ClientSummary[]>([]);

  const [portfolio, setPortfolio] = useState<PortfolioMetrics>({
    totalClients: 0,
    activeProjects: 0,
    totalRevenue: 0,
    avgUtilization: 0,
    billableHours: 0,
    nonBillableHours: 0
  });

  const [revenue, setRevenue] = useState<RevenueMetrics>({
    currentMonth: 0,
    previousMonth: 0,
    forecast: 0,
    mrr: 0
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch agency metrics from API
        const response = await Make_Authorized_API_Call<{
          clients: ClientSummary[];
          portfolio: PortfolioMetrics;
          revenue: RevenueMetrics;
        }>(API_ENDPOINTS.AGENCYFLOW.METRICS, 'GET');

        if (isSuccessResponse(response)) {
          const metrics = response.data.Records[0];

          if (metrics) {
            setClients(metrics.clients || []);
            setPortfolio(metrics.portfolio || portfolio);
            setRevenue(metrics.revenue || revenue);
          }
        } else {
          throw new Error(response.message || 'Failed to load metrics');
        }

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
        console.error('[AgencyFlowDashboard] Error:', errorMessage);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderMetric = (value: number | string, loading: boolean) => {
    if (loading) return <span className="text-gray-400">--</span>;
    return value;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Agency Dashboard</h1>
        <p className="text-gray-600">AgencyFlow Client & Revenue Management</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p className="font-semibold">Error loading dashboard</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Portfolio Overview */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Portfolio Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Active Clients</p>
            <p className="text-2xl font-bold" style={{ color: brandColor }}>
              {renderMetric(portfolio.totalClients, loading)}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Active Projects</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(portfolio.activeProjects, loading)}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Avg Utilization</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(`${portfolio.avgUtilization}%`, loading)}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Total Revenue</p>
            <p className="text-2xl font-bold text-gray-900">
              {loading ? '--' : formatCurrency(portfolio.totalRevenue)}
            </p>
          </div>
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Tracking</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">This Month</p>
            <p className="text-2xl font-bold" style={{ color: brandColor }}>
              {loading ? '--' : formatCurrency(revenue.currentMonth)}
            </p>
            {!loading && revenue.previousMonth > 0 && (
              <p className={`text-sm ${revenue.currentMonth >= revenue.previousMonth ? 'text-green-600' : 'text-red-600'}`}>
                {revenue.currentMonth >= revenue.previousMonth ? '+' : ''}
                {Math.round(((revenue.currentMonth - revenue.previousMonth) / revenue.previousMonth) * 100)}% vs last month
              </p>
            )}
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Monthly Recurring</p>
            <p className="text-2xl font-bold text-gray-900">
              {loading ? '--' : formatCurrency(revenue.mrr)}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Forecast (Next 30d)</p>
            <p className="text-2xl font-bold text-gray-900">
              {loading ? '--' : formatCurrency(revenue.forecast)}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Billable Hours</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(portfolio.billableHours, loading)}
            </p>
            <p className="text-sm text-gray-500">
              {!loading && `${portfolio.nonBillableHours} non-billable`}
            </p>
          </div>
        </div>
      </div>

      {/* Client Portfolio */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Portfolio</h2>
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading clients...</div>
          ) : clients.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No clients found. Add your first client to get started.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projects
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {clients.map((client) => (
                  <tr key={client.Client_ID} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{client.Client_Name}</div>
                      <div className="text-sm text-gray-500">{client.Client_ID}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(client.status)}`}>
                        {client.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {client.activeProjects}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {client.hoursThisMonth}h
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${Math.min(client.utilizationRate, 100)}%`,
                              backgroundColor: brandColor
                            }}
                          />
                        </div>
                        <span className="text-sm text-gray-900">{client.utilizationRate}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(client.totalRevenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Utilization Chart Placeholder */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Resource Utilization</h2>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
          <p className="text-gray-500">
            {loading ? 'Loading chart data...' : 'Utilization chart visualization'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgencyFlowDashboard;
